import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Loader2, Eye, Clock, Users, Film } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const RANGES = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function fmtSeconds(total) {
  const t = Number(total || 0);
  if (t <= 0) return "0m";
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function StudioAnalytics() {
  const [tenant, setTenant] = useState(null);
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/tenant/me").then((r) => alive && setTenant(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/cms/analytics/summary?range=${range}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range]);

  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";

  const chartData = useMemo(() => {
    if (!data?.views_by_day) return [];
    return data.views_by_day.map((d) => ({
      date: fmtDate(d.date),
      views: d.views,
      minutes: Math.round((d.watch_seconds || 0) / 60),
    }));
  }, [data]);

  const dropOffData = useMemo(() => data?.drop_off_curve || [], [data]);

  return (
    <StudioLayout tenant={tenant}>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap" data-testid="studio-analytics-header">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Analytics</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Audience &amp; engagement</h1>
          <p className="text-white/50 mt-1 text-sm">Views, watch time, drop-off, and what your audience is actually finishing.</p>
        </div>
        <RangeSelector range={range} setRange={setRange} themeColor={themeColor} />
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20 text-white/40 text-sm uppercase tracking-widest" data-testid="studio-analytics-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="studio-analytics-kpis">
            <Kpi label="Total views" value={data.total_views.toLocaleString()} icon={<Eye className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-total-views" />
            <Kpi label="Watch time" value={fmtSeconds(data.total_watch_seconds)} icon={<Clock className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-watch-time" />
            <Kpi label="Unique viewers" value={data.unique_viewers.toLocaleString()} icon={<Users className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-unique-viewers" />
            <Kpi label="Top titles" value={(data.top_titles || []).length} icon={<Film className="w-3.5 h-3.5" />} accent={accentColor} testid="kpi-top-titles" />
          </div>

          {/* Views by day chart */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="chart-views-by-day">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Views by day</div>
                <h3 className="font-display font-bold text-lg mt-1">Engagement curve</h3>
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-white/50">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ backgroundColor: themeColor }} /> Views</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ backgroundColor: accentColor }} /> Minutes watched</span>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="#ffffff14" vertical={false} />
                  <XAxis dataKey="date" stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #ffffff20", borderRadius: 0, fontSize: 12 }}
                    labelStyle={{ color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                  />
                  <Line type="monotone" dataKey="views" stroke={themeColor} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="minutes" stroke={accentColor} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Top titles */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="top-titles-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top titles</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Most-watched in this window</h3>
              {(data.top_titles || []).length === 0 ? (
                <EmptyState text="No views yet in this range." />
              ) : (
                <div className="space-y-2">
                  {data.top_titles.map((t, idx) => (
                    <Link
                      key={t.content_item_id}
                      to={`/studio/catalogue/${t.content_item_id}`}
                      className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/20 transition-colors"
                      data-testid={`top-title-${t.slug}`}
                    >
                      <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5">{t.kind}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{t.views}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">views</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{fmtSeconds(t.watch_seconds)}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">watched</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Top reels */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="top-reels-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top reels</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Short-form pull</h3>
              {(data.top_reels || []).length === 0 ? (
                <EmptyState text="No reel views yet in this range." />
              ) : (
                <div className="space-y-2">
                  {data.top_reels.map((t, idx) => (
                    <Link
                      key={t.content_item_id}
                      to={`/studio/catalogue/${t.content_item_id}`}
                      className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/20 transition-colors"
                      data-testid={`top-reel-${t.slug}`}
                    >
                      <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5">reel</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{t.views}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">views</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Drop-off curve */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="chart-drop-off">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Drop-off</div>
            <h3 className="font-display font-bold text-lg mt-1 mb-5">Where viewers stop watching</h3>
            {dropOffData.length === 0 ? (
              <EmptyState text="Not enough completed sessions yet to compute drop-off." />
            ) : (
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={dropOffData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                    <CartesianGrid stroke="#ffffff14" vertical={false} />
                    <XAxis dataKey="bucket" stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={10} />
                    <YAxis stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #ffffff20", borderRadius: 0, fontSize: 12 }}
                      labelStyle={{ color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                    />
                    <Bar dataKey="view_count" fill={themeColor} radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="text-[11px] text-white/40 mt-3">Each bar is the share of completed sessions that stopped within that progress bucket.</p>
          </div>
        </>
      )}
    </StudioLayout>
  );
}

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/45">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="mt-3 font-display font-bold text-2xl tracking-tight">{value}</div>
    </div>
  );
}

function RangeSelector({ range, setRange, themeColor }) {
  return (
    <div className="inline-flex border border-white/10 bg-[#121212]" data-testid="analytics-range-selector">
      {RANGES.map((r) => {
        const active = range === r.id;
        return (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors ${active ? "text-black" : "text-white/60 hover:text-white"}`}
            style={active ? { backgroundColor: themeColor } : {}}
            data-testid={`range-${r.id}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-white/10 p-8 text-center text-white/45 text-sm">
      {text}
    </div>
  );
}
